import React, { JSX, Suspense, useMemo } from "react";

import { Box, Theme, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useHistoryChartQuery } from "src/api/locationChartQueries.api";
import ChartSkeleton from "src/components/ChartSkeleton/ChartSkeleton";
import { dashboardBlockChartTooltipFormatter } from "src/components/Tooltip/helpers/dashboardBlockChartTooltipFormatter";
import type IntervalType from "src/constants/intervalType.enum";
import MeasureType from "src/constants/measureType.enum";
import SeriesType from "src/constants/seriesType.enum";
import { type LocationModel } from "src/types/location";

import { type ChartTooltipFormatterParams } from "../../interfaces/DashboardComponentParams";

const ReactECharts = React.lazy(() => import("echarts-for-react"));

const styles = (theme: Theme) => ({
  chartContainer: { height: theme.spacing(11.25) },
});

function DashboardBlockLineChart({
  intervalType,
  selectedLocation,
}: {
  intervalType: IntervalType;
  selectedLocation: LocationModel;
}): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();

  const { data: measuresData } = useHistoryChartQuery(selectedLocation?.Id, intervalType, null, MeasureType.Occupancy);

  const chartData = useMemo(
    () => (measuresData?.data ? measuresData.data.map((item) => (item.currentValue === null ? { ...item, currentValue: 0 } : item)) : []),
    [measuresData?.data]
  );

  const option = useMemo(
    () => ({
      grid: {
        left: "0%",
        right: "0%",
        bottom: "3%",
        top: "0%",
        containLabel: true,
      },
      tooltip: {
        confine: true,
        trigger: "axis",
        formatter: (params: ChartTooltipFormatterParams[]) => dashboardBlockChartTooltipFormatter(params, theme),
      },
      xAxis: [
        {
          type: "time",
          axisLabel: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
      ],
      yAxis: [
        {
          type: "value",
          axisLabel: {
            show: false,
          },
          splitLine: {
            show: false,
          },
        },
      ],
      series: [
        {
          name: `${t("visitors")}`,
          type: SeriesType.Line,
          showSymbol: false,
          emphasis: {
            focus: "series",
          },
          label: {
            show: false,
          },
          data: chartData.map((e) => [e.currentDate, e.currentValue]),
          itemStyle: {
            color: theme.palette.primary.main,
          },
        },
      ],
    }),
    [chartData, theme, t]
  );

  return (
    <Box sx={styles(theme).chartContainer}>
      <Suspense fallback={<ChartSkeleton />}>
        {measuresData != null && <ReactECharts option={option} />}
        {measuresData == null && <ChartSkeleton />}
      </Suspense>
    </Box>
  );
}
export default DashboardBlockLineChart;
